import React from "react";
import "../POS/PoPrintTemplate.css";

const ITEMS_PER_PAGE = 25;

export default function PoPrintTemplate({ po, pdfMode = false }) {
  if (!po) return null;

  const items = po.items || [];

  const currency = po.Currency || po.Supplier?.Currency || "USD";

  const total = items.reduce((sum, item) => {
    return sum + (parseFloat(item.ExtendedPrice) || 0);
  }, 0);

  function formatDate(dateStr) {
    if (!dateStr) return "—";

    const date = new Date(dateStr);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleDateString("en-PH", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  }

  function formatNumber(value) {
    return Number(value || 0).toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  }

  function formatMoney(value) {
    return `${currency} ${Number(value || 0).toLocaleString("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  }

  function chunkArray(array, size) {
    const chunks = [];

    for (let i = 0; i < array.length; i += size) {
      chunks.push(array.slice(i, i + size));
    }

    return chunks;
  }

  const itemPages = chunkArray(items, ITEMS_PER_PAGE);
  const pages = itemPages.length > 0 ? itemPages : [[]];

  return (
    <div className="po-document">
      {pages.map((pageItems, pageIndex) => {
        const isFirstPage = pageIndex === 0;
        const isLastPage = pageIndex === pages.length - 1;
        const startNo = pageIndex * ITEMS_PER_PAGE;

        return (
          <div
            key={pageIndex}
            className={`po-page ${!isLastPage ? "page-break" : ""}`}
          >
            <div className={`po-print ${pdfMode ? "pdf-mode" : ""}`}>
              {isFirstPage && (
                <>
                  <div className="po-header">
                    <div className="po-company">
                      <img
                        src="/img/biocare-logo.png"
                        alt="Biocare Logo"
                        className="po-logo"
                      />

                      <h2>Biocare Inc.</h2>

                      <p>
                        Rm. 306 Webjet Bldg. No. 64 Quezon Ave cor BMA Ave.
                        <br />
                        Tatalon, Quezon City 1113, Philippines
                      </p>
                    </div>

                    <div className="po-title">
                      <h2>Purchase Order</h2>

                      <p>
                        <strong>PO Number:</strong> {po.PONumber || "—"}
                      </p>
                    </div>
                  </div>

                  <div className="po-supplier">
                    <div className="supplier-left">
                      <h1>{po.Supplier?.SupplierName || po.SupplierName || "—"}</h1>

                      <p>
                        {po.Supplier?.Address ||
                          po.Address ||
                          "No address provided"}
                      </p>

                      <br />

                      <p>
                        <strong>Email:</strong> {po.Email || "—"}
                      </p>

                      <p>
                        <strong>Phone:</strong> {po.Phone || "—"}
                      </p>

                      <p>
                        <strong>Attn:</strong> {po.Attn || "—"}
                      </p>

                      <p>
                        <strong>Currency:</strong> {currency}
                      </p>
                    </div>

                    <div className="supplier-right">
                      <p>
                        <strong>Payment Terms:</strong>{" "}
                        {po.PaymentTerms || "—"}
                      </p>

                      <p>
                        <strong>Delivery Terms:</strong>{" "}
                        {po.DeliveryTerms || "—"}
                      </p>

                      <p>
                        <strong>Mode of Transport:</strong>{" "}
                        {po.ModeOfTransport || "—"}
                      </p>

                      <p>
                        <strong>Ship Via:</strong> {po.ShipVia || "—"}
                      </p>

                      <p>
                        <strong>Order Date:</strong> {formatDate(po.OrderDate)}
                      </p>

                      <p>
                        <strong>PR No:</strong> {po.PRNo || "—"}
                      </p>

                      <p>
                        <strong>PR Date:</strong> {formatDate(po.PRDate)}
                      </p>
                    </div>
                  </div>

                  <div className="po-billship">
                    <div className="bill-to">
                      <h4>Bill To:</h4>

                      <p>
                        <strong>Biocare Inc.</strong>
                      </p>

                      <p>
                        Rm. 306 Webjet Bldg. No. 64 Quezon Ave cor BMA Ave.
                        Tatalon, Quezon City 1113, Philippines
                      </p>

                      <p>
                        <strong>Phone:</strong> +632 87125389 / +632 82411723
                      </p>

                      <p>
                        <strong>Email:</strong> sgf.logistics@biocare.ph /
                        purchasing@biocare-inc.ph
                      </p>

                      <p>
                        <strong>Attention:</strong> Shirlene G. Franco
                      </p>
                    </div>

                    <div className="ship-to">
                      <h4>Ship To:</h4>

                      <p>
                        <strong>Biocare Inc.</strong>
                      </p>

                      <p>
                        Rm. 306 Webjet Bldg. No. 64 Quezon Ave cor BMA Ave.
                        Tatalon, Quezon City 1113, Philippines
                      </p>

                      <p>
                        <strong>Phone:</strong> +632 87125389 / +632 82411723
                      </p>

                      <p>
                        <strong>Email:</strong> sgf.logistics@biocare.ph /
                        purchasing@biocare-inc.ph
                      </p>

                      <p>
                        <strong>Attention:</strong> Shirlene G. Franco
                      </p>

                      <p>
                        <strong>Request Delivery Date:</strong>{" "}
                        {formatDate(po.RequestDeliveryDate)}
                      </p>
                    </div>
                  </div>
                </>
              )}

              {!isFirstPage && (
                <div className="po-continuation-title">
                  <div>
                    <strong>Purchase Order Items Continuation</strong>
                  </div>

                  <div>
                    <strong>PO Number:</strong> {po.PONumber || "—"}
                  </div>
                </div>
              )}

              <table className="po-table">
                <thead>
                  <tr>
                    <th>No</th>
                    <th>Catalog No</th>
                    <th>Item &amp; Description</th>
                    <th>Qty</th>
                    <th>Unit Price</th>
                    <th>Extended Price</th>
                  </tr>
                </thead>

                <tbody>
                  {pageItems.length > 0 ? (
                    pageItems.map((item, index) => (
                      <tr key={item.PODetailID || `${pageIndex}-${index}`}>
                        <td>{startNo + index + 1}</td>

                        <td>{item.CatalogNo || "—"}</td>

                        <td className="item-description">
                          {item.MachineName ||
                            item.Description ||
                            item.ItemName ||
                            "—"}
                        </td>

                        <td>
                          {item.OrderedQty ? `${item.OrderedQty} kits` : "—"}
                        </td>

                        <td>{formatNumber(item.UnitPrice)}</td>

                        <td>{formatNumber(item.ExtendedPrice)}</td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="6" className="no-items">
                        No items found
                      </td>
                    </tr>
                  )}
                </tbody>

                {isLastPage && (
                  <tfoot>
                    <tr>
                      <td colSpan="5" className="total-label">
                        <strong>Total:</strong>
                      </td>

                      <td>
                        <strong>{formatMoney(total)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>

              {isLastPage && po.Comments && (
                <div className="po-comments">
                  <p>
                    <strong>Purchase Order Comments:</strong>
                  </p>

                  <p>{po.Comments}</p>
                </div>
              )}

              {isLastPage && (
                <div className="po-signatures">
                  <div className="signature-block">
                    <p>
                      <strong>Prepared By:</strong>
                    </p>

                    <p>
                      <strong>Shirlene G. Franco</strong>
                      <br />
                      Purchasing Officer
                    </p>
                  </div>

                  <div className="signature-block approved-block">
                    <p>
                      <strong>Approved By:</strong>
                    </p>

                    <div className="po-approved-row">
                      <div>
                        <p>
                          <strong>Zenaida F. Villaflor</strong>
                          <br />
                          Logistics Manager
                          <br />
                          <small>Authorized Signature</small>
                        </p>
                      </div>

                      <div>
                        <p>
                          <strong>Eleanor Panaglima</strong>
                          <br />
                          Finance Manager
                          <br />
                          <small>Authorized Signature</small>
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}